import { useState, useEffect } from 'react'
import { ReactPortal } from '../../../components/reactPortal'
import { Modal } from '../../../components/modal/modal'
import { useSelector, useDispatch } from '../../../hooks/redux'
import { CustomButton } from '../../../components/customButton'
import { Checkbox } from '../../../components/checkbox/checkbox'
import { CustomIcon } from '../../../components/customIcon/customIcon'
import { setModalVisibility, setDefaultModalSettings } from '../../../store/slices/admin/adminSlice'
import { getUserVoiceAccess, updateUserVoiceAccess, getVoiceGroups } from '../../../store/slices/voiceGroups/async'
import { IVoiceGroup } from '../../../store/slices/voiceGroups/voiceGroupsSlice'
import ttsClasses from '../../../styles/tts/ttsModal.module.scss'
import classes from '../../../styles/admin/voiceGroupsModal.module.scss'
import cn from 'classnames'

export const VoiceGroupsModal = () => {
  const dispatch = useDispatch()
  const modal = useSelector((state) => state.admin.modal)
  const groups = useSelector((state) => state.voiceGroups.groups)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number[]>([])

  const isOpen = modal.type === 'voiceGroups'

  useEffect(() => {
    if (isOpen && modal.user.user_id) {
      dispatch(getVoiceGroups() as any)
      dispatch(getUserVoiceAccess(modal.user.user_id) as any).then((action: any) => {
        const voiceGroups: { voice_group_id: number }[] = action.payload?.voice_groups ?? []
        setSelected(voiceGroups.map((g) => g.voice_group_id))
      })
    }
  }, [isOpen, modal.user.user_id])

  const handleClose = () => {
    dispatch(setModalVisibility('none'))
    dispatch(setDefaultModalSettings())
    setSearch('')
    setSelected([])
  }

  const handleSave = async () => {
    await dispatch(
      updateUserVoiceAccess({
        user_id: modal.user.user_id!,
        voice_group_ids: selected,
      }) as any
    )
    handleClose()
  }

  const isAdmin = modal.user.access_admin

  const defaultGroupId = groups.find((g) => g.is_default)?.voice_group_id

  const toggleGroup = (id: number) => {
    if (id === 69 && isAdmin) return
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
      if (next.length === 0 && defaultGroupId !== undefined) return [defaultGroupId]
      return next
    })
  }

  const matchesSearch = (g: IVoiceGroup) => g.name.toLowerCase().includes(search.toLowerCase())
  const systemGroups = [...groups.filter((g) => g.is_system)].reverse().filter(matchesSearch)
  const customGroups = groups.filter((g) => !g.is_system).filter(matchesSearch)

  return (
    <ReactPortal>
      <Modal
        classes={{ modalContent: cn(ttsClasses.ttsModal, ttsClasses.TtsModalSettings) }}
        title={`Доступ к группам ${modal.user.auth_username ?? ''}`}
        isOpen={isOpen}
        handleClose={handleClose}
      >
        <div className={classes.content}>
          <div className={classes.search}>
            <CustomIcon icon="magnifier" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Название группы"
              className={classes.searchInput}
            />
          </div>
          <div className={classes.list}>
            {systemGroups.map((group) => {
              const icon = group.voice_group_id === 69 ? 'crown' : 'ttsLogo'
              const iconClass = cn(classes.itemIcon, icon === 'crown' && classes.itemIconSmall)
              return (
                <div
                  key={group.voice_group_id}
                  className={cn(
                    classes.item,
                    selected.includes(group.voice_group_id) && classes.itemSelected,
                    group.voice_group_id === 69 && isAdmin && classes.itemDisabled
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    toggleGroup(group.voice_group_id)
                  }}
                >
                  <Checkbox isChecked={selected.includes(group.voice_group_id)} handleCheckboxChange={() => {}} />
                  <span className={iconClass}>
                    <CustomIcon icon={icon} />
                  </span>
                  <span className={cn(classes.itemName, classes.itemNameSystem)}>{group.name}</span>
                  <span className={classes.itemCount}>
                    {group.voices.length}{' '}
                    {group.voices.length === 1 ? 'голос' : group.voices.length < 5 ? 'голоса' : 'голосов'}
                  </span>
                </div>
              )
            })}
            {systemGroups.length > 0 && <div className={classes.divider} />}
            {customGroups.map((group) => (
              <div
                key={group.voice_group_id}
                className={cn(classes.item, selected.includes(group.voice_group_id) && classes.itemSelected)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  toggleGroup(group.voice_group_id)
                }}
              >
                <Checkbox isChecked={selected.includes(group.voice_group_id)} handleCheckboxChange={() => {}} />
                <span className={classes.itemName}>{group.name}</span>
                <span className={classes.itemCount}>
                  {group.voices.length}{' '}
                  {group.voices.length === 1 ? 'голос' : group.voices.length < 5 ? 'голоса' : 'голосов'}
                </span>
              </div>
            ))}
          </div>
          <div className={classes.footer}>
            <CustomButton onClick={handleClose}>Отмена</CustomButton>
            <CustomButton viewType="primary" onClick={handleSave}>
              Сохранить
            </CustomButton>
          </div>
        </div>
      </Modal>
    </ReactPortal>
  )
}
